import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createTransfer, listTransferLines, listTransfers, postTransfer } from '../../api/inventoryOps';
import { listItems, listUoms } from '../../api/masterData';
import { listWarehouses } from '../../api/orgData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { DocumentStatus, Item, Transfer, TransferLine, Uom, Warehouse } from '../../types/domain';

const TRANSFER_STATUSES: DocumentStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];

/**
 * Inter-warehouse Transfer — create a DRAFT transfer (fn_create_transfer)
 * then post it (fn_post_transfer_checked), which writes TRANSFER_OUT at the
 * source warehouse and TRANSFER_IN at the destination to stock_ledger, and
 * rejects the post if the source warehouse doesn't have enough stock.
 * Migration 0033, permission-gated on the source warehouse's branch.
 */
export default function TransfersPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('transfer', 'create');
  const canPost = hasPermission('transfer', 'post');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [transferRows, itemRows, uomRows, whRows] = await Promise.all([
        listTransfers(activeBranchId ? activeBranchWarehouseIds : undefined),
        listItems(),
        listUoms(),
        listWarehouses(),
      ]);
      setTransfers(transferRows);
      setItems(itemRows);
      setUoms(uomRows);
      setWarehouses(whRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Transfers has no branch_id column — scoped via the active branch's
  // warehouse ids (source OR dest touching one of them). Re-fetches once
  // those ids resolve and again on any branch change.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, activeBranchWarehouseIds.join(',')]);

  const warehouseName = (id: string) => warehouses.find((w) => w.warehouse_id === id)?.name ?? id;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transfers.filter((t) => {
      if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
      if (q && !t.transfer_number.toLowerCase().includes(q) && !warehouseName(t.source_warehouse_id).toLowerCase().includes(q) && !warehouseName(t.dest_warehouse_id).toLowerCase().includes(q)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfers, search, statusFilter, warehouses]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);
  const transferCounts = statusCounts(transfers, TRANSFER_STATUSES);

  const handlePost = async (transferId: string) => {
    setActionError(null);
    setPostingId(transferId);
    try {
      await postTransfer(transferId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setPostingId(null);
    }
  };

  return (
    <div>
      <PageHeader title="Transfers" subtitle="Perpindahan stok antar gudang — posting menulis TRANSFER_OUT/IN ke Stock Ledger. Migration 0010/0033." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={warehouses.length < 2}>
          {showForm ? 'Batal' : '+ Transfer baru'}
        </Button>
      )}
      {canCreate && warehouses.length < 2 && !loading && <ErrorNote text="Butuh minimal 2 gudang aktif untuk membuat transfer." />}
      {showForm && canCreate && (
        <NewTransferForm
          items={items}
          uoms={uoms}
          warehouses={warehouses}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && transfers.length > 0 && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Transfer', value: transfers.length },
              { label: 'Draft', value: transferCounts.DRAFT, tone: transferCounts.DRAFT > 0 ? 'warning' : 'success' },
              { label: 'Posted', value: transferCounts.POSTED, tone: 'success' },
              { label: 'Cancelled', value: transferCounts.CANCELLED, tone: 'neutral' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari no. transfer atau gudang..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            }
          />
        </>
      )}
      {!loading && !error && transfers.length === 0 && <EmptyState text="Belum ada transfer." />}
      {!loading && !error && transfers.length > 0 && total === 0 && <EmptyState text="Tidak ada transfer yang cocok dengan pencarian/filter." />}
      {total > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>No. Transfer</th>
              <th style={thStyle}>Dari</th>
              <th style={thStyle}>Ke</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((t) => (
              <ClickableRow key={t.transfer_id} onClick={() => setSelectedTransfer(t)}>
                <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{t.transfer_number}</td>
                <td style={tdStyle}>{warehouseName(t.source_warehouse_id)}</td>
                <td style={tdStyle}>{warehouseName(t.dest_warehouse_id)}</td>
                <td style={tdStyle}>
                  <StatusBadge status={t.status} />
                </td>
                <td style={tdStyle}>
                  {canPost && t.status === 'DRAFT' && (
                    <Button
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handlePost(t.transfer_id);
                      }}
                      loading={postingId === t.transfer_id}
                    >
                      Post ke Stock Ledger
                    </Button>
                  )}
                </td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}
      <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />

      <TransferDetailDrawer transfer={selectedTransfer} items={items} uoms={uoms} warehouseName={warehouseName} onClose={() => setSelectedTransfer(null)} />
    </div>
  );
}

function TransferDetailDrawer({
  transfer,
  items,
  uoms,
  warehouseName,
  onClose,
}: {
  transfer: Transfer | null;
  items: Item[];
  uoms: Uom[];
  warehouseName: (id: string) => string;
  onClose: () => void;
}) {
  const [lines, setLines] = useState<TransferLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!transfer) return;
    setLines(null);
    setError(null);
    listTransferLines(transfer.transfer_id)
      .then(setLines)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [transfer]);

  const itemLabel = (id: string) => {
    const it = items.find((i) => i.item_id === id);
    return it ? `${it.item_code} — ${it.name}` : id;
  };
  const uomLabel = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;

  return (
    <Drawer open={!!transfer} onClose={onClose} title={transfer ? `Transfer ${transfer.transfer_number}` : 'Detail Transfer'}>
      {transfer && (
        <div>
          <DrawerField label="No. Transfer" value={transfer.transfer_number} />
          <DrawerField label="Status" value={<StatusBadge status={transfer.status} />} />
          <DrawerField label="Dari gudang" value={warehouseName(transfer.source_warehouse_id)} />
          <DrawerField label="Ke gudang" value={warehouseName(transfer.dest_warehouse_id)} />
          <DrawerField label="Diposting" value={transfer.posted_at ? formatDateTime(transfer.posted_at) : 'Belum diposting'} />
          <DrawerField label="Dibuat" value={formatDateTime(transfer.created_at)} />

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Baris Transfer</div>
          {error && <ErrorNote text={error} />}
          {!error && lines === null && <LoadingNote text="Memuat baris transfer..." />}
          {lines && lines.length === 0 && <EmptyState text="Tidak ada baris pada transfer ini." />}
          {lines && lines.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Qty</th>
                  <th style={thStyle}>UOM</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.transfer_line_id}>
                    <td style={tdStyle}>{itemLabel(l.item_id)}</td>
                    <td style={tdStyle}>{l.qty}</td>
                    <td style={tdStyle}>{uomLabel(l.uom_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}

interface DraftLine {
  itemId: string;
  uomId: string;
  qty: string;
}

function NewTransferForm({
  items,
  uoms,
  warehouses,
  onCreated,
}: {
  items: Item[];
  uoms: Uom[];
  warehouses: Warehouse[];
  onCreated: () => void;
}) {
  const { context } = usePermissions();
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ itemId: '', uomId: '', qty: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { itemId: '', uomId: '', qty: '' }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!context || !context.authenticated) return;
    setSubmitting(true);
    setError(null);
    try {
      await createTransfer({
        tenantId: context.tenant_id,
        sourceWarehouseId: sourceId,
        destWarehouseId: destId,
        lines: lines.filter((l) => l.itemId && l.uomId && l.qty).map((l) => ({ item_id: l.itemId, uom_id: l.uomId, qty: Number(l.qty) })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Dari gudang">
            <Select value={sourceId} onChange={(e) => setSourceId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id} disabled={w.warehouse_id === destId}>
                  {w.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Ke gudang">
            <Select value={destId} onChange={(e) => setDestId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id} disabled={w.warehouse_id === sourceId}>
                  {w.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 10 }}>
          <div style={{ flex: 2 }}>
            <FieldLabel label="Item">
              <Select value={line.itemId} onChange={(e) => updateLine(i, { itemId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {items.map((it) => (
                  <option key={it.item_id} value={it.item_id}>
                    {it.item_code} — {it.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 100 }}>
            <FieldLabel label="UOM">
              <Select value={line.uomId} onChange={(e) => updateLine(i, { uomId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {uoms.map((u) => (
                  <option key={u.uom_id} value={u.uom_id}>
                    {u.code}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 100 }}>
            <FieldLabel label="Qty">
              <TextInput type="number" min="0.01" step="0.01" value={line.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} required />
            </FieldLabel>
          </div>
          {lines.length > 1 && (
            <Button type="button" variant="ghost" onClick={() => removeLine(i)}>
              Hapus
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={addLine} style={{ marginBottom: 12 }}>
        + Baris
      </Button>
      <div>
        <Button type="submit" variant="primary" loading={submitting} disabled={!sourceId || !destId || sourceId === destId}>
          Buat Transfer (DRAFT)
        </Button>
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
